import React, { useState, useEffect } from 'react'
import { subscribeToWallPosts, deleteWallPost } from '../services/firestore'
import { useAuth } from '../context/AuthContext'
import { MessageSquare, Clock, ImageIcon, Link as LinkIcon, Trash2 } from 'lucide-react'
import { capitalizeName } from '../utils/formatters'
import './Wall.css'

const Wall = () => {
    const { user } = useAuth()
    const [posts, setPosts] = useState([])
    const [loading, setLoading] = useState(true)
    const [isDeleting, setIsDeleting] = useState(null)

    useEffect(() => {
        // Subscribe to all wall posts
        const unsubscribe = subscribeToWallPosts((result) => {
            if (result.success) {
                setPosts(result.data)
            } else {
                console.error('Error cargando el muro:', result.error)
            }
            setLoading(false)
        })

        return () => unsubscribe()
    }, [])

    const formatDate = (timestamp) => {
        if (!timestamp) return 'Recientemente'
        const date = timestamp.toDate()
        return new Intl.DateTimeFormat('es-HN', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        }).format(date)
    }

    // Helper to safely render text with newlines
    const renderContent = (content) => {
        if (!content) return null
        return content.split('\n').map((line, i) => (
            <React.Fragment key={i}>
                {line}
                {i !== content.split('\n').length - 1 && <br />}
            </React.Fragment>
        ))
    }

    const handleDelete = async (postId) => {
        if (!window.confirm('¿Estás seguro de que quieres eliminar esta publicación? Esta acción no se puede deshacer.')) {
            return
        }

        setIsDeleting(postId)
        try {
            const result = await deleteWallPost(postId)
            if (!result.success) {
                alert('Error al eliminar la publicación: ' + result.error)
            }
        } catch (error) {
            console.error(error)
            alert('Ocurrió un error inesperado al eliminar')
        } finally {
            setIsDeleting(null)
        }
    }

    return (
        <div className="wall-page">
            <main className="wall-main">
                <div className="wall-hero">
                    <div className="wall-hero-content">
                        <h1>Muro VTIC</h1>
                        <p>Descubre compendios, recursos e investigaciones valiosas compartidas por nuestros increíbles tutores para toda la comunidad.</p>
                    </div>
                </div>

                <div className="wall-container">
                    {loading ? (
                        <div className="wall-loading">
                            <div className="spinner"></div>
                            <p>Cargando publicaciones...</p>
                        </div>
                    ) : posts.length === 0 ? (
                        <div className="wall-empty">
                            <MessageSquare size={48} />
                            <h2>Aún no hay publicaciones</h2>
                            <p>Pronto nuestros tutores empezarán a compartir recursos fascinantes por aquí. ¡Vuelve pronto!</p>
                        </div>
                    ) : (
                        <div className="posts-feed">
                            {posts.map(post => (
                                <article key={post.id} className="post-card">
                                    <header className="post-header">
                                        <img
                                            src={post.authorAvatar || 'https://ui-avatars.com/api/?name=' + encodeURIComponent(post.authorName || 'Tutor')}
                                            alt={capitalizeName(post.authorName)}
                                            className="post-avatar"
                                        />
                                        <div className="post-author-info">
                                            <h3>{capitalizeName(post.authorName)}</h3>
                                            <time>
                                                <Clock size={14} />
                                                {formatDate(post.createdAt)}
                                            </time>
                                        </div>
                                        {user && user.uid === post.createdBy && (
                                            <button
                                                className="post-delete-btn"
                                                onClick={() => handleDelete(post.id)}
                                                disabled={isDeleting === post.id}
                                                aria-label="Eliminar publicación"
                                            >
                                                <Trash2 size={18} />
                                                {isDeleting === post.id ? '...' : ''}
                                            </button>
                                        )}
                                    </header>

                                    <div className="post-content">
                                        <p>{renderContent(post.content)}</p>

                                        {post.linkUrl && (
                                            <a href={post.linkUrl} target="_blank" rel="noopener noreferrer" className="post-link">
                                                <div className="post-link-icon">
                                                    <LinkIcon size={20} />
                                                </div>
                                                <div className="post-link-info">
                                                    <span className="post-link-title">{post.linkTitle || 'Ver enlace adjunto'}</span>
                                                    <span className="post-link-url">{post.linkUrl}</span>
                                                </div>
                                            </a>
                                        )}
                                    </div>

                                    {post.imageUrl && (
                                        <div className="post-image-container">
                                            <img src={post.imageUrl} alt="Contenido adjunto" className="post-image" loading="lazy" />
                                        </div>
                                    )}
                                </article>
                            ))}
                        </div>
                    )}
                </div>
            </main>
        </div>
    )
}

export default Wall
